import { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Settings,
  User,
  Shield,
  Bell,
  Eye,
  EyeOff,
  Save,
  CreditCard,
  Calendar,
  CheckCircle,
  AlertCircle,
  Building2,
  Users,
  Globe,
} from "lucide-react";
import "../../../styles/modern-design-system.css";
import toast from "../../../utils/toast";
import { useUser } from "../../../context/UserContext";
import useItineraryLimits from "../../../hooks/useItineraryLimits";
import { canSeeAgencyAdmin } from "../../../config/subscriptionConfig";
import UsageDashboard from "../../../components/agent/UsageDashboard";
import { validatePassword, getPasswordErrorMessage, getUnmetRequirements } from "../../../utils/passwordPolicy";
import PageHeader from "../../../components/ui/PageHeader";
import BaseModal from "../../../components/ui/BaseModal";

export default function ModernSettings() {
  const { user, updateUser, usageSummary, loadingUsage, planDisplayName } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Subscription limits
  const {
    usage,
    canCreate,
    recommendations,
    loading: limitsLoading,
    refreshLimits,
  } = useItineraryLimits();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [stripeSubscription, setStripeSubscription] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalType, setPasswordModalType] = useState("success"); // "success" or "error"
  const [passwordModalMessage, setPasswordModalMessage] = useState("");

  // Track if changes have been made
  const [hasProfileChanges, setHasProfileChanges] = useState(false);
  const [hasPasswordChanges, setHasPasswordChanges] = useState(false);
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);

  // Check if user has agency admin access (plan-gated, not role-only)
  const hasAgencyAdminAccess = () => canSeeAgencyAdmin(user);

  // Form states
  const [formData, setFormData] = useState({
    firstName: user?.firstname || "",
    lastName: user?.lastname || "",
    email: user?.email || "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    notifications: {
      email: true,
      push: true,
      marketing: false,
    },
  });

  // Agency settings state (only for agency admins)
  const [agencySettings, setAgencySettings] = useState({
    businessInfo: {
      agencyName: "",
      businessType: "",
      licenseNumber: "",
      address: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      phone: "",
      email: "",
      website: "",
    },
    userManagement: {
      allowSelfRegistration: false,
      requireEmailVerification: true,
      defaultRole: "agentUser",
      maxAgents: 50,
    },
    agencySecurity: {
      requireTwoFactor: false,
      sessionTimeout: 30,
      passwordPolicy: "strong",
      allowPasswordReset: true,
    },
    integrations: {
      emailService: "smtp",
      smtpHost: "",
      smtpPort: 587,
      smtpUsername: "",
      smtpPassword: "",
      calendarIntegration: false,
      paymentProcessor: "stripe",
    },
  });

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  // Activate tab from ?tab= query param (e.g. links from ItineraryLimitModal / UsageDashboard)
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  // Refresh subscription when returning from subscription page
  useEffect(() => {
    if (location.state?.subscriptionUpdated) {
      fetchStripeSubscription();
      refreshLimits();
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, refreshLimits]);

  // Load agency settings for admins
  useEffect(() => {
    if (hasAgencyAdminAccess()) {
      fetchAgencySettings();
    }
    fetchStripeSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchAgencySettings = async () => {
    try {
      // Mock data for now - replace with actual API call
      setAgencySettings({
        businessInfo: {
          agencyName: "Paradise Travel Agency",
          businessType: "Travel Agency",
          licenseNumber: "TA-2024-001",
          address: "123 Travel Street",
          city: "Miami",
          state: "FL",
          zipCode: "33101",
          country: "United States",
          phone: "+1 (555) 123-4567",
          email: "info@paradise-travel.com",
          website: "https://paradise-travel.com",
        },
        userManagement: {
          allowSelfRegistration: false,
          requireEmailVerification: true,
          defaultRole: "agentUser",
          maxAgents: 50,
        },
        agencySecurity: {
          requireTwoFactor: false,
          sessionTimeout: 30,
          passwordPolicy: "strong",
          allowPasswordReset: true,
        },
        integrations: {
          emailService: "smtp",
          smtpHost: "smtp.gmail.com",
          smtpPort: 587,
          smtpUsername: "noreply@paradise-travel.com",
          smtpPassword: "",
          calendarIntegration: false,
          paymentProcessor: "stripe",
        },
      });
    } catch (error) {
      console.error("Error fetching agency settings:", error);
    }
  };

  // Track profile changes
  useEffect(() => {
    if (user) {
      const hasChanges =
        formData.firstName !== (user.firstname || "") ||
        formData.lastName !== (user.lastname || "") ||
        formData.email !== (user.email || "");
      setHasProfileChanges(hasChanges);
    }
  }, [formData.firstName, formData.lastName, formData.email, user]);

  // Track password changes
  useEffect(() => {
    const hasChanges =
      formData.currentPassword !== "" ||
      formData.newPassword !== "" ||
      formData.confirmPassword !== "";
    setHasPasswordChanges(hasChanges);
  }, [
    formData.currentPassword,
    formData.newPassword,
    formData.confirmPassword,
  ]);

  // Track notification changes
  useEffect(() => {
    // Compare against default notification settings
    const defaultNotifications = {
      email: true,
      push: true,
      marketing: false,
    };

    const hasChanges =
      formData.notifications.email !== defaultNotifications.email ||
      formData.notifications.push !== defaultNotifications.push ||
      formData.notifications.marketing !== defaultNotifications.marketing;

    setHasNotificationChanges(hasChanges);
  }, [formData.notifications]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNotificationChange = (type, value) => {
    setFormData((prev) => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: value,
      },
    }));
  };

  const handleAgencyInputChange = (section, field, value) => {
    setAgencySettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSaveAgencySettings = async () => {
    setLoading(true);
    try {
      // In real implementation, save to API
      await new Promise((resolve) => setTimeout(resolve, 1000));
      if (import.meta.env.DEV) {
        // Agency settings saved
      }
      toast.success("Agency settings updated successfully!");
    } catch {
      toast.error("Failed to save agency settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchStripeSubscription = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/stripe/subscription-status`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setStripeSubscription(data.subscription);
      }
    } catch (error) {
      console.error("Failed to fetch subscription status:", error);
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/stripe/cancel-subscription`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            cancelAtPeriodEnd: true,
            reason: cancelReason,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowCancelModal(false);
        setCancelReason("");
        // Refresh subscription status
        await fetchStripeSubscription();
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to cancel subscription");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/stripe/reactivate-subscription`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        // Refresh subscription status
        await fetchStripeSubscription();
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to reactivate subscription");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      // For now, just show success message since notifications API endpoint doesn't exist
      // TODO: Implement actual notifications API when backend is ready
      if (import.meta.env.DEV) {
      }

      // Simulate API call delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      toast.success("Notification preferences updated successfully!");
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      // Ensure we only send profile data, not password data
      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      };

      if (import.meta.env.DEV) {
      }

      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(profileData),
      });

      if (res.ok) {
        const updatedUser = await res.json();
        updateUser(updatedUser);
        // Immediately disable the button by resetting changes
        setHasProfileChanges(false);
        // Show success modal
        setShowSuccessModal(true);
      } else {
        const error = await res.json();
        toast.error(error.message || "Failed to update profile");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (formData.newPassword !== formData.confirmPassword) {
      setPasswordModalType("error");
      setPasswordModalMessage("New passwords don't match. Please try again.");
      setShowPasswordModal(true);
      return;
    }

    // Validate new password using unified policy
    const passwordValidation = validatePassword(formData.newPassword);
    if (!passwordValidation.ok) {
      const errorMsg = getPasswordErrorMessage(formData.newPassword);
      setPasswordModalType("error");
      setPasswordModalMessage(errorMsg || "Password does not meet requirements");
      setShowPasswordModal(true);
      return;
    }

    setLoading(true);
    try {
      // Ensure we only send password data, not profile data
      const passwordData = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      };

      if (import.meta.env.DEV) {
      }

      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(passwordData),
      });

      if (res.ok) {
        // Immediately disable the button by resetting changes
        setHasPasswordChanges(false);
        // Clear password fields
        setFormData((prev) => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        }));
        // Show success modal
        setPasswordModalType("success");
        setPasswordModalMessage("Your password has been changed successfully.");
        setShowPasswordModal(true);
      } else {
        const error = await res.json();
        setPasswordModalType("error");
        setPasswordModalMessage(error.message || "Failed to change password. Please try again.");
        setShowPasswordModal(true);
      }
    } catch {
      setPasswordModalType("error");
      setPasswordModalMessage("Network error. Please try again.");
      setShowPasswordModal(true);
    } finally {
      setLoading(false);
    }
  };

  const baseTabs = [
    { id: "profile", label: "Profile", shortLabel: "Profile", icon: User },
    { id: "security", label: "Security", shortLabel: "Security", icon: Shield },
    {
      id: "subscription",
      label: "Subscription",
      shortLabel: "Plan",
      icon: CreditCard,
    },
    {
      id: "notifications",
      label: "Notifications",
      shortLabel: "Notif",
      icon: Bell,
      disabled: true,
      comingSoon: true,
    },
  ];

  const agencyTabs = [
    {
      id: "business",
      label: "Business Info",
      shortLabel: "Business",
      icon: Building2,
    },
    {
      id: "users",
      label: "User Management",
      shortLabel: "Users",
      icon: Users,
    },
    {
      id: "agency-security",
      label: "Agency Security",
      shortLabel: "Security",
      icon: Shield,
    },
    {
      id: "integrations",
      label: "Integrations",
      shortLabel: "Integrations",
      icon: Globe,
    },
  ];


  const renderTabContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Personal Information
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Update your personal details and contact information.
              </p>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className="px-3 w-full h-10 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    className="px-3 w-full h-10 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your last name"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="px-3 w-full h-10 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your email address"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    This email will be used for account notifications and
                    password recovery.
                  </p>
                </div>
              </div>

              <div className="pt-6 mt-8 border-t border-gray-200">
                <button
                  onClick={handleSaveProfile}
                  disabled={loading || !hasProfileChanges}
                  className="flex items-center px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        );

      case "security":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Password Security
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Keep your account secure by updating your password regularly.
              </p>

              <div className="space-y-6 max-w-md">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.currentPassword}
                      onChange={(e) =>
                        handleInputChange("currentPassword", e.target.value)
                      }
                      className="px-3 pr-12 w-full h-10 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="flex absolute inset-y-0 right-0 items-center pr-3 text-gray-400 transition-colors hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) =>
                      handleInputChange("newPassword", e.target.value)
                    }
                    className="px-3 w-full h-10 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter your new password"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Password must be at least 12 characters long and include uppercase, lowercase, and a number.
                  </p>
                  {formData.newPassword && !validatePassword(formData.newPassword).ok && (
                    <div className="mt-2 text-xs text-gray-600">
                      <p className="font-medium">Requirements:</p>
                      <ul className="list-disc list-inside mt-1 space-y-0.5">
                        {getUnmetRequirements(formData.newPassword).map((req, idx) => (
                          <li key={idx}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    className="px-3 w-full h-10 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm your new password"
                  />
                </div>

                <div className="pt-4">
                  <button
                    onClick={handleChangePassword}
                    disabled={loading || !hasPasswordChanges}
                    className="flex items-center px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-red-700 rounded-lg shadow-sm transition-all duration-200 hover:from-red-500 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
                  >
                    {loading ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case "subscription":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Subscription & Usage
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Manage your subscription plan and monitor your itinerary usage.
              </p>

              {/* Usage Dashboard - Clean and Compact */}
              <UsageDashboard usageSummary={usageSummary} loadingUsage={loadingUsage} />

              {/* Upgrade Recommendation */}
              {recommendations && recommendations.should_upgrade && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Upgrade Recommended
                      </h4>
                      <p className="mt-1 text-sm text-gray-600">
                        {recommendations.reason}. Consider upgrading to our{" "}
                        {recommendations.recommended_plan} plan for more
                        itineraries.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Limit Reached Warning */}
              {!canCreate && !limitsLoading && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Monthly Limit Reached
                      </h4>
                      <p className="mt-1 text-sm text-gray-600">
                        You've used all {usage?.monthly_limit} itineraries for
                        this month. Upgrade to create more.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cancellation Warning */}
              {stripeSubscription?.cancel_at_period_end && (
                <div className="p-4 mt-6 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Subscription Scheduled for Cancellation
                      </h4>
                      <p className="mt-1 text-sm text-gray-600">
                        Your subscription will be cancelled on{" "}
                        {stripeSubscription.cancel_at
                          ? new Date(
                              stripeSubscription.cancel_at * 1000
                            ).toLocaleDateString()
                          : new Date(
                              stripeSubscription.current_period_end
                            ).toLocaleDateString()}
                        . You'll retain access until then.
                      </p>
                      <button
                        onClick={handleReactivateSubscription}
                        disabled={loading}
                        className="mt-3 text-sm font-medium text-orange-700 underline hover:text-orange-800"
                      >
                        {loading
                          ? "Reactivating..."
                          : "Reactivate Subscription"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col gap-3 pt-6 mt-6 border-t border-gray-200 sm:flex-row">
                <button
                  onClick={() => navigate('/agent/settings/subscription')}
                  className="flex-1 flex justify-center items-center px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg shadow-sm transition-all hover:bg-blue-700"
                >
                  <CreditCard className="mr-2 w-4 h-4" />
                  Manage Subscription
                </button>

                <button
                  onClick={() =>
                    (window.location.href =
                      "mailto:support@parkproit.com?subject=Subscription%20Support")
                  }
                  className="flex-1 flex justify-center items-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 transition-all hover:bg-gray-50"
                >
                  Contact Support
                </button>
              </div>

              {/* Cancel Subscription (Agency Admins Only) */}
              {hasAgencyAdminAccess() &&
                stripeSubscription &&
                !stripeSubscription.cancel_at_period_end && (
                  <div className="pt-6 mt-6 border-t border-gray-200">
                    <h4 className="mb-2 text-sm font-semibold text-gray-900">
                      Danger Zone
                    </h4>
                    <p className="mb-4 text-sm text-gray-600">
                      Cancel your subscription. You'll retain access until the
                      end of your billing period.
                    </p>
                    <button
                      onClick={() => setShowCancelModal(true)}
                      disabled={loading}
                      className="px-6 py-2.5 text-sm font-medium text-red-700 bg-white rounded-lg border-2 border-red-300 shadow-sm transition-all hover:bg-red-50 hover:border-red-400 disabled:opacity-50"
                    >
                      Cancel Subscription
                    </button>
                  </div>
                )}

              {/* Key Benefits - Simplified */}
              <div className="p-4 mt-6 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="mb-3 text-sm font-semibold text-gray-900">
                  Your Plan Includes
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="flex-shrink-0 mr-2 w-4 h-4 text-green-600" />
                    <span>
                      {usage?.monthly_limit === -1
                        ? "Unlimited itineraries"
                        : `${usage?.monthly_limit || 5} itineraries per month`}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="flex-shrink-0 mr-2 w-4 h-4 text-green-600" />
                    <span>Client questionnaire links</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700">
                    <CheckCircle className="flex-shrink-0 mr-2 w-4 h-4 text-green-600" />
                    <span>PDF exports</span>
                  </div>
                </div>
                <p className="pt-3 mt-3 text-xs text-gray-600 border-t border-gray-300">
                  Need more features? Upgrade your plan to unlock additional
                  capabilities.
                </p>
              </div>
            </div>
          </div>
        );

      case "notifications":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Notification Preferences
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Choose how you'd like to receive updates and alerts from ParkPro.
              </p>
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="p-4 mb-4 bg-blue-50 rounded-full">
                  <Bell className="w-8 h-8 text-blue-500" />
                </div>
                <h4 className="mb-2 text-base font-semibold text-gray-900">
                  Coming Soon
                </h4>
                <p className="max-w-sm text-sm text-gray-500">
                  Notification preferences are on the way. You'll be able to
                  control email, push, and in-app alerts here.
                </p>
              </div>
            </div>
          </div>
        );

      case "business":
        return (
          <div className="space-y-8">
            <div>
              <h3 className="mb-1 text-lg font-semibold text-gray-900">
                Business Information
              </h3>
              <p className="text-sm text-gray-500">
                Manage your agency's details and contact information.
              </p>
            </div>

            {/* Agency identity */}
            <div className="space-y-5">
              <h4 className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
                Agency
              </h4>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Agency Name
                  </label>
                  <input
                    type="text"
                    value={agencySettings.businessInfo.agencyName}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "agencyName", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Your Travel Agency"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Business Type
                  </label>
                  <select
                    value={agencySettings.businessInfo.businessType}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "businessType", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors appearance-none cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Travel Agency">Travel Agency</option>
                    <option value="Independent Agent">Independent Agent</option>
                    <option value="Travel Consultant">Travel Consultant</option>
                  </select>
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    License / IATA Number
                  </label>
                  <input
                    type="text"
                    value={agencySettings.businessInfo.licenseNumber}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "licenseNumber", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. 12345678"
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-5">
              <h4 className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
                Contact
              </h4>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={agencySettings.businessInfo.phone}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "phone", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={agencySettings.businessInfo.email}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "email", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="info@your-agency.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Website
                  </label>
                  <input
                    type="url"
                    value={agencySettings.businessInfo.website}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "website", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://your-agency.com"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="space-y-5">
              <h4 className="text-xs font-semibold tracking-wider text-gray-400 uppercase">
                Address
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={agencySettings.businessInfo.address}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "address", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="123 Main Street"
                  />
                </div>
                {/* City / State / ZIP on same row — proportional widths */}
                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-3">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      City
                    </label>
                    <input
                      type="text"
                      value={agencySettings.businessInfo.city}
                      onChange={(e) =>
                        handleAgencyInputChange("businessInfo", "city", e.target.value)
                      }
                      className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Miami"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      State
                    </label>
                    <input
                      type="text"
                      value={agencySettings.businessInfo.state}
                      onChange={(e) =>
                        handleAgencyInputChange("businessInfo", "state", e.target.value)
                      }
                      className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="FL"
                      maxLength={2}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      ZIP Code
                    </label>
                    <input
                      type="text"
                      value={agencySettings.businessInfo.zipCode}
                      onChange={(e) =>
                        handleAgencyInputChange("businessInfo", "zipCode", e.target.value)
                      }
                      className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="33101"
                    />
                  </div>
                </div>
                <div className="max-w-xs">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Country
                  </label>
                  <input
                    type="text"
                    value={agencySettings.businessInfo.country}
                    onChange={(e) =>
                      handleAgencyInputChange("businessInfo", "country", e.target.value)
                    }
                    className="h-10 px-4 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="United States"
                  />
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <button
                onClick={handleSaveAgencySettings}
                disabled={loading}
                className="flex items-center px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        );

      case "users":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Team Members
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Invite agents and manage who has access to your agency.
              </p>

              {/* Seat usage from subscription plan */}
              <div className="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Users className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Agent Seats
                      </p>
                      <p className="text-xs text-gray-500">
                        Included with your {planDisplayName} plan
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-blue-700">
                    {(user?.AgencyMaxUsers ?? 1)}{" "}
                    {(user?.AgencyMaxUsers ?? 1) === 1 ? "seat" : "seats"}
                  </span>
                </div>
              </div>

              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="p-4 mb-4 bg-gray-50 rounded-full">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <h4 className="mb-2 text-base font-semibold text-gray-900">
                  Team management is in the Agency Admin section
                </h4>
                <p className="mb-6 max-w-sm text-sm text-gray-500">
                  Invite agents, manage roles, and view your team's activity
                  from the Agency Admin panel.
                </p>
                <button
                  onClick={() => navigate("/agent/admin/users")}
                  className="flex items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
                >
                  <Users className="mr-2 w-4 h-4" />
                  Go to Team Management
                </button>
              </div>
            </div>
          </div>
        );

      case "agency-security":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Agency Security
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Configure security policies for your agency.
              </p>

              <div className="max-w-sm space-y-6">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Session Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    value={agencySettings.agencySecurity.sessionTimeout}
                    onChange={(e) =>
                      handleAgencyInputChange(
                        "agencySecurity",
                        "sessionTimeout",
                        parseInt(e.target.value)
                      )
                    }
                    className="px-3 w-full h-10 text-sm text-gray-900 bg-white rounded-lg border border-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="5"
                    max="480"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Agents will be signed out after this period of inactivity.
                  </p>
                </div>
              </div>

              {/* Coming Soon security features */}
              <div className="p-4 mt-8 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="mb-2 text-sm font-semibold text-gray-700">
                  Coming Soon
                </h4>
                <ul className="space-y-1 text-sm text-gray-500 list-disc list-inside">
                  <li>Two-factor authentication enforcement</li>
                  <li>Password policy requirements for all agents</li>
                  <li>IP allowlist</li>
                </ul>
              </div>

              <div className="pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={handleSaveAgencySettings}
                  disabled={loading}
                  className="flex items-center px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        );

      case "integrations":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="mb-4 text-lg font-semibold text-gray-900">
                Integrations
              </h3>
              <p className="mb-6 text-sm text-gray-600">
                Connect ParkPro with your other tools and services.
              </p>

              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="p-4 mb-4 bg-blue-50 rounded-full">
                  <Globe className="w-8 h-8 text-blue-500" />
                </div>
                <h4 className="mb-2 text-base font-semibold text-gray-900">
                  Integrations Coming Soon
                </h4>
                <p className="mb-8 max-w-sm text-sm text-gray-500">
                  We're building out integrations to connect your agency's
                  tools. Here's what's planned:
                </p>
                <div className="grid grid-cols-1 gap-3 w-full max-w-sm text-left">
                  {[
                    { label: "Google / Apple Calendar Sync", Icon: Calendar },
                      { label: "Zapier Webhooks", Icon: Globe },
                  ].map(({ label, Icon }) => (
                    <div
                      key={label}
                      className="flex items-center px-4 py-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <Icon className="mr-3 w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{label}</span>
                      <span className="ml-auto px-2 py-0.5 text-xs font-medium text-gray-500 bg-gray-200 rounded-full">
                        Soon
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div>
        {/* Header */}
        <PageHeader
          breadcrumbs={[
            { label: 'Dashboard', href: '/agent/dashboard' },
            { label: 'Settings' },
          ]}
          title="Settings"
          subtitle="Manage your account preferences and security settings"
        />

        {/* Settings Content with Sidebar Navigation */}
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Sidebar Navigation */}
          <div className="flex-shrink-0 lg:w-64">
            <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
              {/* Personal Settings Section */}
              <div className="p-4 border-b border-gray-200">
                <h3 className="mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                  Personal
                </h3>
                <nav className="space-y-1">
                  {baseTabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => !tab.disabled && setActiveTab(tab.id)}
                        disabled={tab.disabled}
                        className={`flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 ${
                          tab.disabled
                            ? "text-gray-400 cursor-not-allowed"
                            : activeTab === tab.id
                            ? "bg-blue-50 text-blue-700 border-l-[3px] border-blue-600"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                      >
                        <div className="flex items-center">
                          <Icon className="mr-3 w-4 h-4" />
                          <span>{tab.label}</span>
                        </div>
                        {tab.comingSoon && (
                          <span className="px-2 py-0.5 text-xs font-medium text-white bg-gray-700 rounded-full">
                            Soon
                          </span>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Agency Admin Section - Only for admins */}
              {hasAgencyAdminAccess() && (
                <div className="p-4">
                  <h3 className="mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                    Agency Admin
                  </h3>
                  <nav className="space-y-1">
                    {agencyTabs.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          className={`flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-150 ${
                            activeTab === tab.id
                              ? "bg-blue-50 text-blue-700 border-l-[3px] border-blue-600"
                              : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                          }`}
                        >
                          <Icon className="mr-3 w-4 h-4" />
                          <span>{tab.label}</span>
                        </button>
                      );
                    })}
                  </nav>
                </div>
              )}
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 min-w-0">
            <div className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="p-4 sm:p-6 lg:p-8">{renderTabContent()}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      <BaseModal
        isOpen={showCancelModal}
        onClose={() => { setShowCancelModal(false); setCancelReason(""); }}
        title="Cancel Subscription"
        size="sm"
      >
        <BaseModal.Body>
          <p className="mb-4 text-sm text-gray-600">
            Are you sure you want to cancel your subscription? You'll retain access until the end of your current billing period.
          </p>
          <div>
            <label className="block mb-1.5 text-sm font-medium text-gray-700">Help us improve (optional)</label>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="What's the main reason for cancelling?"
              className="px-3 py-2 w-full text-sm text-gray-900 bg-white rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows="3"
            />
          </div>
        </BaseModal.Body>
        <BaseModal.Footer>
          <button
            onClick={() => { setShowCancelModal(false); setCancelReason(""); }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50"
            type="button"
          >
            Keep Subscription
          </button>
          <button
            onClick={handleCancelSubscription}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 disabled:opacity-50"
            type="button"
          >
            {loading ? "Cancelling..." : "Confirm Cancellation"}
          </button>
        </BaseModal.Footer>
      </BaseModal>


      {/* Success Modal */}
      <BaseModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} size="sm" showClose={false}>
        <BaseModal.Body className="text-center">
          <div className="flex justify-center items-center mx-auto mb-3 w-12 h-12 bg-green-100 rounded-full">
            <CheckCircle className="w-6 h-6 text-green-600" />
          </div>
          <h3 className="mb-1 text-lg font-semibold text-gray-900">Changes Saved</h3>
          <p className="mb-4 text-sm text-gray-500">Your personal information has been updated successfully.</p>
          <button
            onClick={() => setShowSuccessModal(false)}
            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
            type="button"
          >
            Done
          </button>
        </BaseModal.Body>
      </BaseModal>

      {/* Password Change Modal (Success/Error) */}
      <BaseModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} size="sm" showClose={false}>
        <BaseModal.Body className="text-center">
          <div className={`flex justify-center items-center mx-auto mb-3 w-12 h-12 rounded-full ${
            passwordModalType === "success" ? "bg-green-100" : "bg-red-100"
          }`}>
            {passwordModalType === "success"
              ? <CheckCircle className="w-6 h-6 text-green-600" />
              : <AlertCircle className="w-6 h-6 text-red-600" />}
          </div>
          <h3 className="mb-1 text-lg font-semibold text-gray-900">
            {passwordModalType === "success" ? "Password Changed" : "Password Change Failed"}
          </h3>
          <p className="mb-4 text-sm text-gray-500">{passwordModalMessage}</p>
          <button
            onClick={() => setShowPasswordModal(false)}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
              passwordModalType === "success" ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700"
            }`}
            type="button"
          >
            {passwordModalType === "success" ? "Done" : "Try Again"}
          </button>
        </BaseModal.Body>
      </BaseModal>
    </>
  );
}